import React from 'react';
import { useUnifiedSubscription } from '../../contexts/SubscriptionContext';
import { SubscriptionBanner } from './SubscriptionBanner';
import { SubscriptionModal } from './SubscriptionModal';
import { SubscriptionAlertBanner } from './SubscriptionAlertBanner';

/**
 * Optimized SubscriptionComponents using UnifiedSubscriptionContext
 * This eliminates the need to access both AuthContext and SubscriptionContext
 */
export const SubscriptionComponents: React.FC = () => {
  const {
    subscription,
    usageDetails,
    banner,
    modal,
    dismissBanner,
    closeSubscriptionModal,
    isPastDue,
    isCanceled,
    isCanceledAtPeriodEnd,
    isAlertBannerVisible,
    dismissAlertBanner
  } = useUnifiedSubscription();

  // Determine alert status for the banner component
  const getAlertStatus = (): 'past_due' | 'canceled' | 'canceling' | null => {
    if (isPastDue) return 'past_due';
    if (isCanceled) return 'canceled';
    if (isCanceledAtPeriodEnd) return 'canceling';
    return null;
  };

  const alertStatus = getAlertStatus();

  return (
    <>
      {/* Subscription Issue Alert Banner - sticky at top */}
      {alertStatus && isAlertBannerVisible && (
        <SubscriptionAlertBanner
          status={alertStatus}
          planName={subscription?.plan_name}
          periodEndDate={subscription?.current_period_end}
          dismissible={alertStatus === 'canceling'}
          onDismiss={dismissAlertBanner}
        />
      )}

      {/* Subscription Banner with Overlay */}
      {banner.show && banner.type && (
        <>
          {/* Dark overlay behind banner - click to dismiss */}
          <div
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-30 animate-in fade-in duration-300 cursor-pointer"
            onClick={dismissBanner}
          />

          {/* Banner positioned in center */}
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-40 w-full max-w-lg mx-auto p-4 animate-in zoom-in-95 slide-in-from-bottom-8 duration-700">
            <SubscriptionBanner
              type={banner.type}
              usage={usageDetails || undefined}
              planName={subscription?.plan_name}
              onDismiss={dismissBanner}
            />
          </div>
        </>
      )}

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={modal.isOpen}
        onClose={closeSubscriptionModal}
        feature={modal.feature}
        reason={modal.reason}
        currentPlan={subscription?.plan_name}
      />
    </>
  );
};